import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import axios from 'axios';
import { stableKey } from '@common/cache/util.ts';

interface IPartnerListProps {
  search: string;
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
}

const partnerListRequest = async (props: IPartnerListProps) => {
  const response = axiosInstance.get<
    paths[`/record/partners`]['get']['responses']['200']['content']['application/json']
  >(`/record/partners`, {
    params: {
      page: props.page,
      limit: props.limit,
      search: props.search,
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
    },
  });
  const data = (await response).data;

  return data;
};

export const usePartnerListQuery = (props: IPartnerListProps) => {
  const query = useQuery({
    queryKey: ['partners', stableKey(props)],
    queryFn: () => partnerListRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

interface IPartnerProps {
  partner_id: string;
}

const partnerRequest = async (props: IPartnerProps) => {
  const response = axiosInstance.get<
    paths[`/partner/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/partner/${props.partner_id}`);
  const data = (await response).data;

  return data;
};

export const usePartnerQuery = (props: IPartnerProps) => {
  const query = useQuery({
    queryKey: ['partner', props.partner_id],
    queryFn: () => partnerRequest(props),
  });

  return query;
};

interface IPartnerHistoryProps {
  page: number;
  limit: number;
  partner_id: string;
  enabled: boolean;
}

const partnerHistoryRequest = async (props: IPartnerHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/partner/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/partner/${props.partner_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePartnerHistoryQuery = (props: IPartnerHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['partnerHistory', stableKey(props)],
    queryFn: () => partnerHistoryRequest(props),
  });

  return query;
};

const partnerShortRequest = async (record_id?: string) => {
  const response = axiosInstance.get<
    paths[`/record/partners/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/partners/short`, {
    params: {
      ...(record_id && { record_id }),
    },
  });
  const data = (await response).data;
  return data;
};

export const usePartnerShortQuery = (record_id?: string) => {
  const orgQuery = useQuery({
    queryKey: ['partnerShort', record_id],
    queryFn: () => partnerShortRequest(record_id),
  });

  return orgQuery;
};

interface IPartnerLogoProps {
  partner_id: string;
}

const partnerLogoRequest = async (
  { partner_id }: IPartnerLogoProps,
  signal?: AbortSignal,
): Promise<Blob | null> => {
  try {
    const { data } = await axiosInstance.get<Blob>(
      `/partner/${partner_id}/logo.webp`,
      { responseType: 'blob', signal, skipNotFoundRedirect: true },
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null; // no logo
    }
    throw err;
  }
};

export const usePartnerLogoQuery = (props: IPartnerLogoProps) => {
  const query = useQuery({
    queryKey: ['partner', props.partner_id, 'logo'],
    queryFn: () => partnerLogoRequest(props),
  });

  return query;
};
